import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MapPicker from "@/components/MapPicker";
import AddressFields from "@/components/AddressFields";
import { compressImage } from "@/lib/image";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Heart } from "@phosphor-icons/react";
import { DEPARTMENTS } from "@/lib/departments";
import CodeSearchForm from "@/components/CodeSearchForm";

export default function FoundPage() {
  const [form, setForm] = useState({ finder_first_name: "", finder_last_name: "", finder_email: "", description: "", photo: null, location_label: "" });
  const [address, setAddress] = useState({ street: "", postal_code: "", city: "" });
  const [addressCenter, setAddressCenter] = useState(null);
  const [pos, setPos] = useState(null);
  const [department, setDepartment] = useState("");
  const [deptTouched, setDeptTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  // Auto-fill department from postal code (only if the user hasn't manually overridden)
  useEffect(() => {
    if (deptTouched) return;
    const cp = (address.postal_code || "").trim();
    if (/^\d{5}$/.test(cp)) {
      const dept = cp.startsWith("97") || cp.startsWith("98") ? cp.slice(0, 3) : cp.slice(0, 2);
      if (DEPARTMENTS.some((d) => d.code === dept)) setDepartment(dept);
    }
  }, [address.postal_code, deptTouched]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Photo trop lourde (max 15 Mo)"); return; }
    try {
      const compressed = await compressImage(file);
      setForm({ ...form, photo: compressed });
    } catch { toast.error("Impossible de lire cette photo"); }
  };

  const submit = async () => {
    if (!form.photo) {
      toast.error("La photo du doudou trouvé est obligatoire"); return;
    }
    if (!form.finder_first_name || !form.finder_last_name || !form.finder_email || !form.description || !pos) {
      toast.error("Prénom, nom, email, description et position sont requis"); return;
    }
    if (!department) {
      toast.error("Merci de sélectionner un département (obligatoire pour les recherches)"); return;
    }
    setSending(true);
    try {
      const labelParts = [address.street, address.postal_code, address.city].filter(Boolean);
      const location_label = labelParts.join(", ") || form.location_label;
      const { data } = await api.post("/public/found-reports", { ...form, location_label, department, lat: pos.lat, lng: pos.lng });
      if (data?.finder_token) {
        localStorage.setItem("finder_report_token", data.finder_token);
        localStorage.setItem(`finder_token_report_${data.report_id}`, data.finder_token);
      }
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <Heart size={64} weight="fill" className="mx-auto text-rose-500" />
          <h1 className="font-display text-3xl font-black text-stone-800 mt-4">Merci !</h1>
          <p className="text-stone-600 mt-3 leading-relaxed">
            Votre signalement est désormais visible par les familles ayant perdu un doudou dans votre région.
            Si l'une d'elles pense reconnaître son doudou, elle vous écrira directement.
          </p>
          <p className="text-sm text-stone-500 mt-6">
            Pour retrouver les messages reçus : <Link to="/mes-messages" className="text-orange-600 font-bold">Mes conversations</Link>
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="font-display text-3xl font-black text-stone-800" data-testid="found-title">J'ai trouvé un doudou</h1>
        <p className="text-stone-600 mt-2">Aidez-nous à le retrouver à sa famille. Aucun compte nécessaire.</p>

        {/* FEATURE FLAG: QR Code scanner — désactivé pour MVP
        <div className="mt-6 bg-gradient-to-br from-rose-500 to-orange-500 text-white rounded-3xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-lg shadow-rose-300/40">
          <div className="flex items-start gap-3">
            <QrCode size={32} weight="duotone" className="shrink-0" />
            <div>
              <p className="font-display font-bold text-lg">Le doudou a une étiquette ?</p>
              <p className="text-white/90 text-sm">Scannez le QR Code, c'est instantané et la famille est prévenue tout de suite.</p>
            </div>
          </div>
          <QrScanButton className="bg-white !text-rose-600 hover:bg-stone-50 shrink-0 px-5 py-5" />
        </div>

        <p className="text-center text-stone-400 text-xs mt-6 mb-2">Pas d'étiquette ? Remplissez simplement le formulaire ci-dessous.</p>
        */}

          <div className="mt-8"><CodeSearchForm /></div>

          <p className="text-center text-stone-500 text-sm mt-6" data-testid="no-label-hint">Pas d'étiquette ? Remplissez simplement le formulaire ci-dessous.</p>

        <div className="mt-6 bg-white rounded-3xl border border-rose-100 p-8 space-y-5">
          <div>
            <Label>Photo du doudou *</Label>
            <label className="mt-2 flex flex-col items-center justify-center w-full min-h-[12rem] rounded-2xl border-2 border-dashed border-rose-200 bg-rose-50/50 cursor-pointer hover:bg-rose-50 overflow-hidden" data-testid="found-photo-upload">
              {form.photo ? <img src={form.photo} alt="aperçu" className="w-full max-h-[28rem] object-contain rounded-2xl" /> : <><Camera size={32} weight="duotone" className="text-rose-400" /><span className="text-sm text-stone-500 mt-2">Cliquez pour ajouter</span></>}
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Prénom *</Label>
              <Input value={form.finder_first_name} onChange={(e) => setForm({ ...form, finder_first_name: e.target.value })} className="rounded-2xl mt-1 py-6" data-testid="found-name-input" />
            </div>
            <div>
              <Label>Nom de famille * <span className="text-xs text-stone-400 font-normal">(privé)</span></Label>
              <Input value={form.finder_last_name} onChange={(e) => setForm({ ...form, finder_last_name: e.target.value })} className="rounded-2xl mt-1 py-6" data-testid="found-lastname-input" />
            </div>
          </div>
          <div>
            <Label>Adresse email * <span className="text-xs text-stone-400 font-normal">(privée, pour être prévenu·e des messages)</span></Label>
            <Input type="email" value={form.finder_email} onChange={(e) => setForm({ ...form, finder_email: e.target.value })} className="rounded-2xl mt-1 py-6" placeholder="vous@exemple.fr" data-testid="found-email-input" />
            <p className="text-xs text-stone-500 mt-1.5 leading-relaxed">
              🔒 Votre nom de famille et email ne sont <strong>jamais affichés</strong> dans le chat — seul votre prénom est partagé avec la famille. Ces informations sont conservées <strong>uniquement à des fins de litige éventuel</strong> (voir nos <a href="/cgu" className="text-orange-600 font-bold">CGU</a> et <a href="/confidentialite" className="text-orange-600 font-bold">politique de confidentialité</a>).
            </p>
          </div>
          <div>
            <Label>Description *</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-2xl mt-1" placeholder="Lapin rose, écharpe rayée..." data-testid="found-description-input" />
          </div>
          <div>
            <Label>Où l'avez-vous trouvé ? *</Label>
            <p className="text-xs text-stone-500 mt-1 mb-2">Indiquez l'adresse (facultatif) <strong>et</strong> cliquez sur la carte pour positionner précisément le lieu de découverte.</p>
            <AddressFields value={address} onChange={setAddress} streetRequired={false}
              onSelect={(s) => s.centre && setAddressCenter({ lat: s.centre.coordinates[1], lng: s.centre.coordinates[0] })} />
            <div className="mt-3">
              <Label>Département *
                <span className="text-xs text-stone-400 font-normal ml-1">(auto-détecté, modifiable)</span>
              </Label>
              <select
                value={department}
                onChange={(e) => { setDepartment(e.target.value); setDeptTouched(true); }}
                required
                className="mt-1 w-full rounded-2xl border-2 border-rose-100 px-4 py-3 bg-white font-semibold focus:outline-none focus:border-orange-400"
                data-testid="found-department-select"
              >
                <option value="">— Choisir un département —</option>
                {DEPARTMENTS.map((d) => <option key={d.code} value={d.code}>{d.code} — {d.name}</option>)}
              </select>
            </div>
            <div className="mt-4"><MapPicker value={pos} onChange={setPos} height={300} pinType="found" centerOn={addressCenter} /></div>
          </div>
          <Button onClick={submit} disabled={sending} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold shadow-lg shadow-orange-500/30" data-testid="found-submit-button">
            {sending ? "Envoi..." : "Publier le signalement"}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
